import { Cart, AddToCartPayload } from "../types/cart";

const CART_STORAGE_KEY = "onyx_anonymous_cart";
const CART_EXPIRATION_DAYS = 7;

/**
 * Generate a unique cart item ID from product ID and variants
 */
export function generateCartItemId(productId: number, variants: AddToCartPayload["variants"]): string {
  const variantString = [variants.style, variants.size, variants.color].filter(Boolean).join("-");
  return `${productId}-${variantString || "default"}`;
}

/**
 * Generate a unique session ID for anonymous users
 */
export function generateSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Save anonymous cart to localStorage with expiration
 */
export function saveAnonymousCart(cart: Cart): void {
  try {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + CART_EXPIRATION_DAYS);

    const cartData = {
      ...cart,
      expiresAt: expiresAt.toISOString(),
    };

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
  } catch (error) {
    console.error("Failed to save cart to localStorage:", error);
  }
}

/**
 * Load anonymous cart from localStorage and validate expiration
 * Returns empty cart if expired or invalid
 */
export function loadAnonymousCart(): Cart {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) {
      return createEmptyCart();
    }

    const cart: Cart = JSON.parse(stored);

    // Check expiration
    if (cart.expiresAt) {
      const expirationDate = new Date(cart.expiresAt);
      if (expirationDate < new Date()) {
        // Cart expired, clear it
        clearAnonymousCart();
        return createEmptyCart();
      }
    }

    // Recalculate totals to ensure consistency
    return {
      ...cart,
      totalItems: cart.items.reduce((sum, item) => sum + item.quantity, 0),
      totalPrice: cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    };
  } catch (error) {
    console.error("Failed to load cart from localStorage:", error);
    return createEmptyCart();
  }
}

/**
 * Clear anonymous cart from localStorage
 */
export function clearAnonymousCart(): void {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear cart from localStorage:", error);
  }
}

/**
 * Create an empty cart object
 */
export function createEmptyCart(userId?: string): Cart {
  return {
    items: [],
    userId,
    sessionId: userId ? undefined : generateSessionId(),
    totalItems: 0,
    totalPrice: 0,
  };
}

/**
 * Check if cart is expired
 */
export function isCartExpired(cart: Cart): boolean {
  if (!cart.expiresAt) return false;

  const expirationDate = new Date(cart.expiresAt);
  return expirationDate < new Date();
}
